/**
 * Nokia-style snake border: born small, grows while advancing, encircles up to 95%
 * of the spotlight perimeter (never closes fully).
 */

const BORDER_ROOT_ID = 'archon-tour-spotlight-border';
const STAGE_PADDING = 8;
const STAGE_RADIUS = 8;
/** Keep stroke, round caps, and glow inside the SVG viewport. */
const STROKE_BLEED = 6;

const MAX_SNAKE_RATIO = 0.95;
const MIN_SNAKE_PX = 12;
const LAP_DURATION_MS = 5000;
/** Time until the snake reaches its maximum length (95% of perimeter). */
const GROWTH_DURATION_MS = 7500;

let rafId = null;
let lastTrackedElement = null;
let lastAnimTime = null;
let headProgress = 0;
let growthElapsed = 0;

function getRoot() {
  return document.getElementById(BORDER_ROOT_ID);
}

function ensureRoot() {
  let root = getRoot();
  if (root) return root;

  root = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  root.id = BORDER_ROOT_ID;
  root.setAttribute('class', 'archon-tour-spotlight-border');
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('overflow', 'visible');

  const track = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  track.setAttribute('class', 'archon-tour-spotlight-border-track');

  const snake = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  snake.setAttribute('class', 'archon-tour-spotlight-border-snake');

  root.appendChild(track);
  root.appendChild(snake);
  document.body.appendChild(root);
  return root;
}

/**
 * Frame around the highlighted element. Prefer outward padding, but inset on any
 * side that would draw outside the viewport (prevents the snake clipping away).
 */
export function computeSpotlightRect(bounds, viewportW, viewportH) {
  const outward = STAGE_PADDING;
  const min = STROKE_BLEED;
  const maxX = Math.max(min, viewportW - STROKE_BLEED);
  const maxY = Math.max(min, viewportH - STROKE_BLEED);

  let x = bounds.x - outward;
  if (x < min) {
    x = Math.min(bounds.x + min, maxX - 4);
  }

  let y = bounds.y - outward;
  if (y < min) {
    y = Math.min(bounds.y + min, maxY - 4);
  }

  let right = bounds.right + outward;
  if (right > maxX) {
    right = Math.max(bounds.right - min, x + 4);
  }

  let bottom = bounds.bottom + outward;
  if (bottom > maxY) {
    bottom = Math.max(bounds.bottom - min, y + 4);
  }

  return {
    x,
    y,
    width: Math.max(right - x, 4),
    height: Math.max(bottom - y, 4),
  };
}

function roundedRectPath(x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  const right = x + width;
  const bottom = y + height;
  return [
    `M ${x + r} ${y}`,
    `H ${right - r}`,
    `A ${r} ${r} 0 0 1 ${right} ${y + r}`,
    `V ${bottom - r}`,
    `A ${r} ${r} 0 0 1 ${right - r} ${bottom}`,
    `H ${x + r}`,
    `A ${r} ${r} 0 0 1 ${x} ${bottom - r}`,
    `V ${y + r}`,
    `A ${r} ${r} 0 0 1 ${x + r} ${y}`,
    'Z',
  ].join(' ');
}

function applyPathGeometry(pathEl, d) {
  pathEl.setAttribute('d', d);
}

function resetSnakeProgress() {
  headProgress = 0;
  growthElapsed = 0;
  lastAnimTime = null;
}

function easeOutQuad(t) {
  return 1 - (1 - t) * (1 - t);
}

function advanceSnake(pathEl, dt) {
  const perimeter = pathEl.getTotalLength();
  if (!perimeter || perimeter <= 0) return;

  const maxLength = perimeter * MAX_SNAKE_RATIO;
  const minLength = Math.max(MIN_SNAKE_PX, perimeter * 0.012);

  growthElapsed += dt;
  const growthT = Math.min(growthElapsed / GROWTH_DURATION_MS, 1);
  const snakeLength = minLength + (maxLength - minLength) * easeOutQuad(growthT);

  headProgress += (perimeter / LAP_DURATION_MS) * dt;

  let tailPosition;
  let visibleLength;

  if (headProgress <= snakeLength) {
    // Born at the origin: body stretches from start toward the advancing head.
    tailPosition = 0;
    visibleLength = Math.max(minLength * 0.35, headProgress);
  } else {
    // Full body sliding along the path, capped at 95% of the perimeter.
    tailPosition = headProgress - snakeLength;
    visibleLength = snakeLength;
  }

  const tailMod = ((tailPosition % perimeter) + perimeter) % perimeter;
  const gap = Math.max(perimeter - visibleLength, 0.001);

  pathEl.style.strokeDasharray = `${visibleLength} ${gap}`;
  pathEl.style.strokeDashoffset = `${-tailMod}`;
}

export function updateSpotlightBorder(element, dt = 0) {
  if (!element || element.id === 'driver-dummy-element') {
    const root = getRoot();
    if (root) root.style.opacity = '0';
    return;
  }

  const root = ensureRoot();
  const bounds = element.getBoundingClientRect();
  if (bounds.width <= 0 && bounds.height <= 0) {
    root.style.opacity = '0';
    return;
  }

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  root.setAttribute('viewBox', `0 0 ${viewportW} ${viewportH}`);
  root.style.opacity = '1';

  const { x, y, width, height } = computeSpotlightRect(bounds, viewportW, viewportH);
  const radius = Math.min(STAGE_RADIUS, width / 2, height / 2);
  const d = roundedRectPath(x, y, width, height, radius);

  const track = root.querySelector('.archon-tour-spotlight-border-track');
  const snake = root.querySelector('.archon-tour-spotlight-border-snake');

  if (element !== lastTrackedElement) {
    resetSnakeProgress();
    lastTrackedElement = element;
  }

  [track, snake].forEach((node) => {
    if (node) applyPathGeometry(node, d);
  });

  if (snake && dt > 0) {
    advanceSnake(snake, dt);
  } else if (snake) {
    advanceSnake(snake, 0);
  }
}

function tick(timestamp) {
  const dt = lastAnimTime == null ? 0 : Math.min(timestamp - lastAnimTime, 32);
  lastAnimTime = timestamp;

  const active = document.querySelector('.driver-active-element');
  updateSpotlightBorder(active, dt);
  rafId = window.requestAnimationFrame(tick);
}

export function startSpotlightBorderTracking() {
  stopSpotlightBorderTracking();
  document.body.classList.add('archon-product-tour-active');
  resetSnakeProgress();
  ensureRoot();
  rafId = window.requestAnimationFrame(tick);
}

export function stopSpotlightBorderTracking() {
  if (rafId != null) {
    window.cancelAnimationFrame(rafId);
    rafId = null;
  }
  lastTrackedElement = null;
  resetSnakeProgress();
  document.body.classList.remove('archon-product-tour-active');
  getRoot()?.remove();
}
